import React, { useState, useRef, useMemo } from "react";
import {
  LayoutDashboard,
  Newspaper,
  Building2,
  Mic2,
  Star,
  ImageIcon,
  Video,
  Save,
  X,
  Plus,
  Users,
  Edit3,
  GalleryThumbnails,
  MessageCircle,
} from "lucide-react";
import { db, storage, auth } from "../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import ilceKoyler from "../json/ilce-koyler.json";
import ProductsSection, {
  uploadAndPrepareProducts,
} from "../components/ProductsSection";
import FirmEditTab from "../components/FirmEditTab";
import CelebPoliticianEditTab from "../components/CelebPoliticianEditTab";
import PhotoVideoEditTab from "../components/PhotoVideoEditTab";
import InterviewEditTab from "../components/InterviewEditTab";
import NewsEditTab from "../components/NewsEditTab";
import PendingFirmsTab from "../components/PendingFirmsTab";
import PendingCommentsTab from "../components/PendingCommentsTab";
import { SEKTOR_DATA, ANA_SEKTORLER } from "../hooks/sektorData";
import { UNLU_KATEGORI_DATA, ANA_KATEGORILER } from "../hooks/unluKategoriData";

const TABS = [
  { id: "news", label: "Haberler", icon: Newspaper },
  { id: "newsEdit", label: "Haberleri Düzenle", icon: Edit3 },
  { id: "firms", label: "Firmalar", icon: Building2 },
  { id: "pending", label: "Firma Onay", icon: Building2 },
  { id: "pendingComments", label: "Yorum Onay", icon: MessageCircle },
  { id: "firmEdit", label: "Firmaları Düzenle", icon: Edit3 },
  { id: "interviews", label: "Röportajlar", icon: Mic2 },
  { id: "interviewEdit", label: "Röportajları Düzenle", icon: Edit3 },
  { id: "celebrities", label: "Sivaslı Ünlüler", icon: Star },
  { id: "politicians", label: "Sivaslı Siyasiler", icon: Users },
  { id: "celebEdit", label: "Ünlü & Siyasi Düzenle", icon: Edit3 },
  { id: "photos", label: "Resimler", icon: ImageIcon },
  { id: "videos", label: "Videolar", icon: Video },
  { id: "mediaEdit", label: "Medya Düzenle", icon: GalleryThumbnails },
];

const ISLETME_TURLERI = [
  "Şahıs Şirketi",
  "Limited Şirketi",
  "Anonim Şirketi",
  "Komandit Şirketi",
  "Kolektif Şirket",
];

const MAX_ABOUT = 5000;

const initialFirmForm = {
  firmaAdi: "",
  telefon: "",
  fax: "",
  gsm: "",
  email: "",
  web: "",
  linkedin: "",
  logoFile: null,
  logoPreviewUrl: "",
  kurulusYili: "",
  isletmeTuru: "",
  yetkiliKisi: "",
  yetkiliPozisyon: "",
  calismaSaatleri: "",
  anahtarKelimeler: [],
  anahtarKelimeInput: "",
  hakkinda: "",
  ulke: "Türkiye",
  sehir: "",
  ilce: "",
  sivasIlce: "",
  sivasKoy: "",
  acikAdres: "",
  secilenSektorler: [],
  sektorInput: "",
  altSektorler: [],
  altSektorInput: "",
  isGold: false,
  isVitrin: false,
};

/* ── küçük UI yardımcıları ── */
const cx = (...list) => list.filter(Boolean).join(" ");

function AdminInput({ label, error, children, span2 }) {
  return (
    <div className={cx(span2 && "md:col-span-2")}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
        </label>
      )}
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function AInput({
  value,
  onChange,
  placeholder,
  type = "text",
  disabled,
  min,
  max,
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      max={max}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        disabled:bg-slate-50 disabled:text-slate-400 placeholder:text-slate-400 transition"
    />
  );
}

function ASelect({ value, onChange, children }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition appearance-none bg-white
        disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
    >
      {children}
    </select>
  );
}

function ATextarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        placeholder:text-slate-400 transition resize-none"
    />
  );
}

function SectionHeading({ children }) {
  return (
    <div className="flex items-center gap-2 pt-2 pb-1">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
        {children}
      </span>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}

/* ── Logo yükleme ── */
async function uploadLogoIfAny(logoFile) {
  if (!logoFile) return null;
  const user = auth.currentUser;
  if (!user) throw new Error("Oturum bulunamadı. Lütfen giriş yapın.");
  const filename = `logo_${Date.now()}_${logoFile.name || "logo.png"}`;
  const storageRef = ref(storage, `firmalar/${user.uid}/${filename}`);
  await uploadBytes(storageRef, logoFile);
  return await getDownloadURL(storageRef);
}

/* ────────────────────────────── FirmTab ────────────────────────────── */
function FirmTab({ loading, setLoading, showStatus }) {
  const [form, setForm] = useState(initialFirmForm);
  const [errors, setErrors] = useState({});
  const fileRef = useRef(null);
  const [products, setProducts] = useState([]);

  const ilceler = useMemo(
    () => [...new Set(ilceKoyler.map((x) => x.ilce))].sort(),
    []
  );

  // Dosyanın üstüne helper ekle (zaten varsa atlat):
  const toSentenceCase = (s = "") =>
    s.charAt(0).toLocaleUpperCase("tr") + s.slice(1).toLocaleLowerCase("tr");

  const koyler = useMemo(() => {
    if (!form.sivasIlce) return [];
    const list = ilceKoyler
      .filter((x) => x.ilce === form.sivasIlce)
      .map((x) => x.koy);
    const merkez = list.filter((k) =>
      k.toLocaleLowerCase("tr").includes("merkez")
    );
    const diger = list
      .filter((k) => !k.toLocaleLowerCase("tr").includes("merkez"))
      .sort((a, b) => a.localeCompare(b, "tr"));
    return [...merkez, ...diger];
  }, [form.sivasIlce]);

  const set = (patch) => setForm((p) => ({ ...p, ...patch }));

  const validate = () => {
    const e = {};
    if (!form.firmaAdi.trim()) e.firmaAdi = "Firma adı zorunludur.";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Geçerli bir e-posta giriniz.";
    if (form.web && !/^https?:\/\//i.test(form.web))
      e.web = "https:// ile başlamalı.";
    if (form.linkedin && !/^https?:\/\//i.test(form.linkedin))
      e.linkedin = "https:// ile başlamalı.";
    if (!form.sehir.trim()) e.sehir = "Şehir zorunludur.";
    if (!form.secilenSektorler.length)
      e.secilenSektorler = "En az bir sektör seçiniz.";
    if (!form.altSektorler.length) e.altSektorler = "En az bir sektör seçiniz.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSelectLogo = (f) => {
    if (!f) return;
    if (form.logoPreviewUrl) URL.revokeObjectURL(form.logoPreviewUrl);
    set({ logoFile: f, logoPreviewUrl: URL.createObjectURL(f) });
  };

  const removeLogo = () => {
    if (form.logoPreviewUrl) URL.revokeObjectURL(form.logoPreviewUrl);
    set({ logoFile: null, logoPreviewUrl: "" });
    if (fileRef.current) fileRef.current.value = "";
  };

  const addKeyword = () => {
    const k = form.anahtarKelimeInput.trim();
    if (!k || form.anahtarKelimeler.includes(k))
      return set({ anahtarKelimeInput: "" });
    set({
      anahtarKelimeler: [...form.anahtarKelimeler, k],
      anahtarKelimeInput: "",
    });
  };

  const addSektor = (s) => {
    const v = s.trim();
    if (!v || form.secilenSektorler.includes(v))
      return set({ sektorInput: "" });
    set({ secilenSektorler: [...form.secilenSektorler, v], sektorInput: "" });
  };

  const addAltSektor = (s) => {
    const v = s.trim();
    if (!v || form.altSektorler.includes(v)) return set({ altSektorInput: "" });
    set({ altSektorler: [...form.altSektorler, v], altSektorInput: "" });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const uploadedLogo = await uploadLogoIfAny(form.logoFile);
      const {
        logoFile,
        logoPreviewUrl,
        anahtarKelimeInput,
        sektorInput,
        altSektorInput,
        ...rest
      } = form;
      const uploadedProducts = await uploadAndPrepareProducts(products);
      await addDoc(collection(db, "firmalar"), {
        ...rest,
        logoUrl: uploadedLogo || form.logoPreviewUrl || "",
        urunler: uploadedProducts,
        createdAt: serverTimestamp(),
        source: "admin",
      });
      if (form.logoPreviewUrl) URL.revokeObjectURL(form.logoPreviewUrl);
      setForm(initialFirmForm);
      setProducts([]);
      if (fileRef.current) fileRef.current.value = "";
      setErrors({});
      showStatus("success", "Firma başarıyla eklendi.");
    } catch (err) {
      console.error(err);
      showStatus("error", "Firma eklenirken hata: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* ── Temel Bilgiler ── */}
      <SectionHeading>Temel Bilgiler</SectionHeading>

      {/* Logo + Firma Adı */}
      <div className="flex items-start gap-5">
        {/* Logo */}
        <div className="shrink-0">
          <div className="relative w-24 h-24 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center hover:border-slate-400 transition cursor-pointer group">
            {form.logoPreviewUrl ? (
              <img
                src={form.logoPreviewUrl}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            ) : (
              <span className="text-xs text-slate-400 group-hover:text-slate-600 transition text-center px-1">
                Logo Yükle
              </span>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={(e) => onSelectLogo(e.target.files?.[0])}
            />
          </div>
          {/* Logo URL */}
          <AdminInput label="Logo URL">
            <AInput
              value={
                form.logoPreviewUrl.startsWith("blob:")
                  ? ""
                  : form.logoPreviewUrl
              }
              onChange={(e) => {
                set({ logoPreviewUrl: e.target.value, logoFile: null });
                if (fileRef.current) fileRef.current.value = "";
              }}
              placeholder="https://…"
              disabled={!!form.logoFile}
            />
          </AdminInput>
          {form.logoPreviewUrl && (
            <button
              type="button"
              onClick={removeLogo}
              className="mt-1.5 text-xs text-red-500 hover:text-red-700 w-full text-center transition"
            >
              Kaldır
            </button>
          )}
        </div>

        {/* Firma adı + e-posta */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
          <AdminInput label="Firma Adı *" error={errors.firmaAdi} span2>
            <AInput
              value={form.firmaAdi}
              onChange={(e) => set({ firmaAdi: e.target.value })}
              placeholder="Örnek Teknoloji A.Ş."
            />
          </AdminInput>
          <AdminInput label="E-posta" error={errors.email}>
            <AInput
              value={form.email}
              onChange={(e) => set({ email: e.target.value })}
              placeholder="ornek@email.com"
            />
          </AdminInput>
          <AdminInput label="Web Sitesi" error={errors.web}>
            <AInput
              value={form.web}
              onChange={(e) => set({ web: e.target.value })}
              placeholder="https://example.com"
            />
          </AdminInput>
        </div>
      </div>

      {/* İletişim */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <AdminInput label="Telefon">
          <AInput
            value={form.telefon}
            onChange={(e) => set({ telefon: e.target.value })}
            placeholder="0 (212) 000 00 00"
          />
        </AdminInput>
        <AdminInput label="Faks">
          <AInput
            value={form.fax}
            onChange={(e) => set({ fax: e.target.value })}
            placeholder="0 (212) 000 00 00"
          />
        </AdminInput>
        <AdminInput label="GSM">
          <AInput
            value={form.gsm}
            onChange={(e) => set({ gsm: e.target.value })}
            placeholder="05XX XXX XX XX"
          />
        </AdminInput>
      </div>

      {/* Diğer iletişim */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <AdminInput label="LinkedIn" error={errors.linkedin}>
          <AInput
            value={form.linkedin}
            onChange={(e) => set({ linkedin: e.target.value })}
            placeholder="https://linkedin.com/company/xxx"
          />
        </AdminInput>
        <AdminInput label="Çalışma Saatleri">
          <AInput
            value={form.calismaSaatleri}
            onChange={(e) => set({ calismaSaatleri: e.target.value })}
            placeholder="Hafta içi 09:00 – 18:00"
          />
        </AdminInput>
      </div>

      {/* Kuruluş / Tür / Yetkili */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <AdminInput label="Kuruluş Yılı">
          <AInput
            type="number"
            min="1800"
            max={new Date().getFullYear()}
            value={form.kurulusYili}
            onChange={(e) => set({ kurulusYili: e.target.value })}
            placeholder="2020"
          />
        </AdminInput>
        <AdminInput label="İşletme Türü">
          <ASelect
            value={form.isletmeTuru}
            onChange={(e) => set({ isletmeTuru: e.target.value })}
          >
            <option value="">Seçiniz</option>
            {ISLETME_TURLERI.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </ASelect>
        </AdminInput>
        <AdminInput label="Yetkili Kişi">
          <AInput
            value={form.yetkiliKisi}
            onChange={(e) => set({ yetkiliKisi: e.target.value })}
            placeholder="Ad Soyad"
          />
        </AdminInput>
        <AdminInput label="Yetkili Pozisyon">
          <AInput
            value={form.yetkiliPozisyon}
            onChange={(e) => set({ yetkiliPozisyon: e.target.value })}
            placeholder="Genel Müdür"
          />
        </AdminInput>
      </div>

      {/* ── Konum ── */}
      <SectionHeading>Konum</SectionHeading>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <AdminInput label="Ülke">
          <AInput
            value={form.ulke}
            onChange={(e) => set({ ulke: e.target.value })}
          />
        </AdminInput>
        <AdminInput label="Şehir / İl *" error={errors.sehir}>
          <AInput
            value={form.sehir}
            onChange={(e) => set({ sehir: e.target.value })}
            placeholder="İstanbul"
          />
        </AdminInput>
        <AdminInput label="İlçe">
          <AInput
            value={form.ilce}
            onChange={(e) => set({ ilce: e.target.value })}
            placeholder="Kadıköy"
          />
        </AdminInput>
      </div>

      <div className="md:col-span-4">
        <p className="text-sm font-medium text-slate-700 mb-3">
          Sivasın hangi ilçe / köyündensiniz?
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <AdminInput label="İlçe">
            <ASelect
              value={form.sivasIlce}
              onChange={(e) => set({ sivasIlce: e.target.value, sivasKoy: "" })}
            >
              <option value="">İlçe seçiniz…</option>
              {ilceler.map((il) => (
                <option key={il} value={il}>
                  {il}
                </option>
              ))}
            </ASelect>
          </AdminInput>

          <AdminInput label="Köy / Mahalle">
            <ASelect
              value={form.sivasKoy}
              onChange={(e) => set({ sivasKoy: e.target.value })}
              disabled={!form.sivasIlce}
            >
              <option value="">
                {form.sivasIlce ? "Köy seçiniz…" : "Önce ilçe seçiniz"}
              </option>
              {koyler.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </ASelect>
          </AdminInput>
        </div>
      </div>

      <AdminInput label="Açık Adres">
        <ATextarea
          value={form.acikAdres}
          onChange={(e) => set({ acikAdres: e.target.value })}
          placeholder="Mahalle, cadde/sokak, bina, kapı no..."
          rows={2}
        />
      </AdminInput>

      {/* ── Sektörler ── */}
      <SectionHeading>Sektörler</SectionHeading>

      <div>
        <div className="flex gap-2">
          <AInput
            value={form.sektorInput}
            onChange={(e) => set({ sektorInput: e.target.value })}
            placeholder="Sektör adı yazın veya aşağıdan seçin…"
          />
          <button
            type="button"
            onClick={() => addSektor(form.sektorInput)}
            className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Ekle
          </button>
        </div>
        {errors.secilenSektorler && (
          <p className="mt-1 text-xs text-red-500">{errors.secilenSektorler}</p>
        )}

        {/* Öneriler */}
        <ASelect
          value=""
          onChange={(e) => {
            if (e.target.value) addSektor(e.target.value);
          }}
        >
          <option value="">Ana sektör seçin…</option>
          {ANA_SEKTORLER.map((s) => (
            <option key={s} value={s}>
              {toSentenceCase(s)}
            </option>
          ))}
        </ASelect>
        {/* Seçilenler */}
        {form.secilenSektorler.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {form.secilenSektorler.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-medium"
              >
                {s}
                <button
                  type="button"
                  onClick={() =>
                    set({
                      secilenSektorler: form.secilenSektorler.filter(
                        (x) => x !== s
                      ),
                    })
                  }
                  className="text-amber-600 hover:text-red-600 transition"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Alt Sektörler ── */}
      <SectionHeading>Alt Sektörler</SectionHeading>

      <div>
        <div className="flex gap-2">
          <AInput
            value={form.altSektorInput}
            onChange={(e) => set({ altSektorInput: e.target.value })}
            placeholder="Listede yoksa yazın…"
          />
          <button
            type="button"
            onClick={() => addAltSektor(form.altSektorInput)}
            className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Ekle
          </button>
        </div>
        {errors.altSektorler && (
          <p className="mt-1 text-xs text-red-500">{errors.altSektorler}</p>
        )}

        {/* Öneriler */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {(() => {
            const altSecenekler =
              form.secilenSektorler.length > 0
                ? [
                    ...new Set(
                      form.secilenSektorler.flatMap((s) => SEKTOR_DATA[s] || [])
                    ),
                  ]
                : [];
            return (
              <ASelect
                value=""
                onChange={(e) => {
                  if (e.target.value) addAltSektor(e.target.value);
                }}
                disabled={altSecenekler.length === 0}
              >
                <option value="">
                  {form.secilenSektorler.length > 0
                    ? "Alt sektör seçin…"
                    : "Önce ana sektör seçin"}
                </option>
                {altSecenekler.map((s) => (
                  <option key={s} value={s}>
                    {toSentenceCase(s)}
                  </option>
                ))}
              </ASelect>
            );
          })()}
        </div>

        {/* Seçilenler */}
        {form.altSektorler.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {form.altSektorler.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-medium"
              >
                {s}
                <button
                  type="button"
                  onClick={() =>
                    set({
                      altSektorler: form.altSektorler.filter((x) => x !== s),
                    })
                  }
                  className="text-amber-600 hover:text-red-600 transition"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Anahtar Kelimeler ── */}
      <SectionHeading>Anahtar Kelimeler</SectionHeading>

      <div>
        <div className="flex gap-2">
          <AInput
            value={form.anahtarKelimeInput}
            onChange={(e) => set({ anahtarKelimeInput: e.target.value })}
            placeholder="Örn: seramik ustası, tamir…"
          />
          <button
            type="button"
            onClick={addKeyword}
            className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Ekle
          </button>
        </div>
        {form.anahtarKelimeler.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {form.anahtarKelimeler.map((k) => (
              <span
                key={k}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-200 rounded-full text-xs"
              >
                {k}
                <button
                  type="button"
                  onClick={() =>
                    set({
                      anahtarKelimeler: form.anahtarKelimeler.filter(
                        (x) => x !== k
                      ),
                    })
                  }
                  className="text-slate-400 hover:text-red-500 transition"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Hakkında ── */}
      <SectionHeading>Firma Hakkında</SectionHeading>

      <div>
        <ATextarea
          value={form.hakkinda}
          onChange={(e) =>
            set({ hakkinda: e.target.value.slice(0, MAX_ABOUT) })
          }
          placeholder="Firmanızı detaylı şekilde anlatın…"
          rows={5}
        />
        <div
          className={cx(
            "text-right text-xs mt-1 tabular-nums",
            MAX_ABOUT - form.hakkinda.length < 200
              ? "text-amber-500"
              : "text-slate-400"
          )}
        >
          {MAX_ABOUT - form.hakkinda.length} karakter kaldı
        </div>
      </div>

      {/* ── Gold Üyelik ── */}
      <SectionHeading>Üyelik Tipi</SectionHeading>

      <label className="flex items-center gap-3 cursor-pointer group w-fit">
        <div className="relative">
          <input
            type="checkbox"
            checked={form.isGold}
            onChange={(e) => set({ isGold: e.target.checked })}
            className="sr-only"
          />
          <div
            className={cx(
              "w-11 h-6 rounded-full transition-colors duration-200",
              form.isGold ? "bg-amber-400" : "bg-slate-200"
            )}
          >
            <div
              className={cx(
                "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200",
                form.isGold ? "translate-x-5" : "translate-x-0"
              )}
            />
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
            {form.isGold && <span>⭐</span>}
            {form.isGold ? "Gold Firma" : "Standart Firma"}
          </div>
          <div className="text-xs text-slate-400">
            {form.isGold
              ? "Vitrin ve öne çıkarma alanlarında gösterilir"
              : "Normal listede yer alır"}
          </div>
        </div>
      </label>
      {/* İkinci toggle — Vitrin */}
      <label className="flex items-center gap-3 cursor-pointer group w-fit">
        <div className="relative">
          <input
            type="checkbox"
            checked={form.isVitrin}
            onChange={(e) => set({ isVitrin: e.target.checked })}
            className="sr-only"
          />
          <div
            className={cx(
              "w-11 h-6 rounded-full transition-colors duration-200",
              form.isVitrin ? "bg-sky-500" : "bg-slate-200"
            )}
          >
            <div
              className={cx(
                "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200",
                form.isVitrin ? "translate-x-5" : "translate-x-0"
              )}
            />
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
            {form.isVitrin && <span>🏪</span>}
            {form.isVitrin ? "Vitrin Firma" : "Vitrin Değil"}
          </div>
          <div className="text-xs text-slate-400">
            {form.isVitrin
              ? "Ana sayfada vitrin alanında öne çıkarılır"
              : "Vitrin alanında gösterilmez"}
          </div>
        </div>
      </label>
      <SectionHeading>Ürünler & Hizmetler</SectionHeading>
      <ProductsSection products={products} setProducts={setProducts} />

      {/* Submit */}
      <div className="pt-2">
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {loading ? "Kaydediliyor…" : "Firmayı Kaydet"}
        </button>
      </div>
    </form>
  );
}

/* ────────────────────────────── AdminPanel ────────────────────────────── */
const AdminPanel = ({ setCurrentPage }) => {
  const [activeTab, setActiveTab] = useState("news");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const photoFileRef = useRef(null);
  const videoFileRef = useRef(null);
  const newsFileRef = useRef(null);
  const extraNewsImageRefs = useRef([null, null, null, null]);
  const [extraNewsImageFiles, setExtraNewsImageFiles] = useState([
    null,
    null,
    null,
    null,
  ]);

  const toSentenceCase = (s = "") =>
    s.charAt(0).toLocaleUpperCase("tr") + s.slice(1).toLocaleLowerCase("tr");

  const ilceler = useMemo(
    () =>
      [...new Set(ilceKoyler.map((x) => x.ilce))].sort((a, b) =>
        a.localeCompare(b, "tr")
      ),
    []
  );
  const ilceKoyMap = useMemo(
    () =>
      ilceKoyler.reduce((acc, r) => {
        if (!acc[r.ilce]) acc[r.ilce] = [];
        acc[r.ilce].push(r.koy);
        return acc;
      }, {}),
    []
  );

  const showStatus = (type, msg) => {
    setStatus({ type, msg });
    setTimeout(() => setStatus(null), 4500);
  };

  /* ── Diğer formlar (değişmedi) ── */
  const [newsForm, setNewsForm] = useState({
    title: "",
    content: "",
    imageUrl: "",
    imageFile: null,
    extraImages: [],
  });
  const [interviewForm, setInterviewForm] = useState({
    title: "",
    guest: "",
    interviewer: "",
    summary: "",
    videoUrl: "",
  });
  const [celebrityForm, setCelebrityForm] = useState({
    name: "",
    kategori: "",
    profession: "",
    birthYear: "",
    sivasIlce: "",
    sivasKoy: "",
    gender: "",
    bio: "",
    imageUrl: "",
    isActive: false,
    isPassive: false,
    isDeceased: false,
    tags: [],
    tagInput: "",
  });

  const [politicianForm, setPoliticianForm] = useState({
    name: "",
    party: "",
    position: "",
    birthYear: "",
    sivasIlce: "",
    sivasKoy: "",
    gender: "",
    bio: "",
    imageUrl: "",
    isActive: false,
    isPassive: false,
    isDeceased: false,
    tags: [],
    tagInput: "",
  });
  const [photoForm, setPhotoForm] = useState({
    title: "",
    description: "",
    imageUrl: "",
    location: "",
    imageFile: null,
  });
  const [videoForm, setVideoForm] = useState({
    title: "",
    description: "",
    videoUrl: "",
    thumbnailUrl: "",
    videoFile: null,
  });

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let finalImageUrl = newsForm.imageUrl;

      if (newsForm.imageFile) {
        const user = auth.currentUser;
        const filename = `news_${Date.now()}_${newsForm.imageFile.name}`;
        const storageRef = ref(storage, `haberler/${user?.uid}/${filename}`);
        await uploadBytes(storageRef, newsForm.imageFile);
        finalImageUrl = await getDownloadURL(storageRef);
      }

      const uploadedExtraImages = await Promise.all(
        (newsForm.extraImages || []).map(async (url, i) => {
          const file = extraNewsImageFiles[i];
          const user = auth.currentUser;
          if (file) {
            const fname = `news_extra_${i}_${Date.now()}_${file.name}`;
            const sRef = ref(storage, `haberler/${user?.uid}/${fname}`);
            await uploadBytes(sRef, file);
            return await getDownloadURL(sRef);
          }
          return url || "";
        })
      );

      const { imageFile, ...rest } = newsForm;
      await addDoc(collection(db, "haberler"), {
        ...rest,
        imageUrl: finalImageUrl,
        extraImages: uploadedExtraImages.filter(Boolean),
        createdAt: serverTimestamp(),
        source: "admin",
      });
      setExtraNewsImageFiles([null, null, null, null]);
      setNewsForm({
        title: "",
        content: "",
        imageUrl: "",
        imageFile: null,
        extraImages: [],
      });
      if (newsFileRef.current) newsFileRef.current.value = "";
      showStatus("success", "Haber başarıyla eklendi.");
    } catch (err) {
      showStatus("error", "Haber eklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleInterviewSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, "roportajlar"), {
        ...interviewForm,
        createdAt: serverTimestamp(),
      });
      setInterviewForm({
        title: "",
        guest: "",
        interviewer: "",
        summary: "",
        videoUrl: "",
      });
      showStatus("success", "Röportaj başarıyla eklendi.");
    } catch (err) {
      showStatus("error", "Röportaj eklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleCelebritySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { tagInput, ...rest } = celebrityForm;
      await addDoc(collection(db, "unluler"), {
        ...rest,
        createdAt: serverTimestamp(),
      });
      setCelebrityForm({
        name: "",
        kategori: "",
        profession: "",
        birthYear: "",
        sivasIlce: "",
        sivasKoy: "",
        gender: "",
        bio: "",
        imageUrl: "",
        tags: [],
        tagInput: "",
      });
      showStatus("success", "Sivaslı ünlü başarıyla eklendi.");
    } catch (err) {
      showStatus("error", "Ünlü eklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handlePoliticianSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { tagInput, ...rest } = politicianForm;
      await addDoc(collection(db, "siyasiler"), {
        ...rest,
        createdAt: serverTimestamp(),
      });
      setPoliticianForm({
        name: "",
        party: "",
        position: "",
        birthYear: "",
        sivasIlce: "",
        sivasKoy: "",
        gender: "",
        bio: "",
        imageUrl: "",
        isActive: false,
        isPassive: false,
        isDeceased: false,
        tags: [],
        tagInput: "",
      });
      showStatus("success", "Siyasetçi başarıyla eklendi.");
    } catch (err) {
      showStatus("error", "Siyasetçi eklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let finalImageUrl = photoForm.imageUrl;

      if (photoForm.imageFile) {
        const user = auth.currentUser;
        const filename = `photo_${Date.now()}_${photoForm.imageFile.name}`;
        const storageRef = ref(storage, `resimler/${user?.uid}/${filename}`);
        await uploadBytes(storageRef, photoForm.imageFile);
        finalImageUrl = await getDownloadURL(storageRef);
      }

      const { imageFile, ...rest } = photoForm;
      await addDoc(collection(db, "resimler"), {
        ...rest,
        imageUrl: finalImageUrl,
        createdAt: serverTimestamp(),
      });
      setPhotoForm({
        title: "",
        description: "",
        imageUrl: "",
        location: "",
        imageFile: null,
      });
      if (photoFileRef.current) photoFileRef.current.value = "";
      showStatus("success", "Resim başarıyla eklendi.");
    } catch (err) {
      showStatus("error", "Resim eklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let finalVideoUrl = videoForm.videoUrl;

      if (videoForm.videoFile) {
        const user = auth.currentUser;
        const filename = `video_${Date.now()}_${videoForm.videoFile.name}`;
        const storageRef = ref(storage, `videolar/${user?.uid}/${filename}`);
        await uploadBytes(storageRef, videoForm.videoFile);
        finalVideoUrl = await getDownloadURL(storageRef);
      }

      const { videoFile, ...rest } = videoForm;
      await addDoc(collection(db, "videolar"), {
        ...rest,
        videoUrl: finalVideoUrl,
        createdAt: serverTimestamp(),
      });
      setVideoForm({
        title: "",
        description: "",
        videoUrl: "",
        thumbnailUrl: "",
        videoFile: null,
      });
      if (videoFileRef.current) videoFileRef.current.value = "";
      showStatus("success", "Video başarıyla eklendi.");
    } catch (err) {
      showStatus("error", "Video eklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Tab içerikleri (news, interviews, celebrities, photos, videos değişmedi) ── */
  const renderContent = () => {
    if (activeTab === "firms") {
      return (
        <FirmTab
          loading={loading}
          setLoading={setLoading}
          showStatus={showStatus}
        />
      );
    }

    if (activeTab === "firmEdit") {
      return (
        <FirmEditTab
          loading={loading}
          setLoading={setLoading}
          showStatus={showStatus}
        />
      );
    }

    if (activeTab === "pending") {
      return <PendingFirmsTab showStatus={showStatus} />;
    }

    if (activeTab === "pendingComments") {
      return <PendingCommentsTab showStatus={showStatus} />;
    }

    if (activeTab === "celebEdit") {
      return (
        <CelebPoliticianEditTab
          loading={loading}
          setLoading={setLoading}
          showStatus={showStatus}
        />
      );
    }

    if (activeTab === "mediaEdit") {
      return (
        <PhotoVideoEditTab
          loading={loading}
          setLoading={setLoading}
          showStatus={showStatus}
        />
      );
    }

    if (activeTab === "interviewEdit") {
      return (
        <InterviewEditTab
          loading={loading}
          setLoading={setLoading}
          showStatus={showStatus}
        />
      );
    }

    if (activeTab === "newsEdit") {
      return (
        <NewsEditTab
          loading={loading}
          setLoading={setLoading}
          showStatus={showStatus}
        />
      );
    }

    if (activeTab === "news")
      return (
        <form onSubmit={handleNewsSubmit} className="space-y-4">
          <AdminInput label="Haber Başlığı" span2>
            <AInput
              value={newsForm.title}
              onChange={(e) =>
                setNewsForm((p) => ({ ...p, title: e.target.value }))
              }
              placeholder="Örn: Sivas'ta yeni yatırım…"
            />
          </AdminInput>
          <AdminInput label="Haber İçeriği">
            <ATextarea
              value={newsForm.content}
              onChange={(e) =>
                setNewsForm((p) => ({ ...p, content: e.target.value }))
              }
              placeholder="Haber metni…"
              rows={5}
            />
          </AdminInput>
          <AdminInput label="Görsel URL">
            <AInput
              value={newsForm.imageUrl}
              onChange={(e) =>
                setNewsForm((p) => ({
                  ...p,
                  imageUrl: e.target.value,
                  imageFile: null,
                }))
              }
              placeholder="https://…"
              disabled={!!newsForm.imageFile}
            />
          </AdminInput>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-xs text-slate-400">veya galeriden seç</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer text-sm text-slate-700 transition">
              <ImageIcon className="w-4 h-4 text-slate-400" />
              {newsForm.imageFile ? newsForm.imageFile.name : "Dosya Seç"}
              <input
                ref={newsFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setNewsForm((p) => ({
                    ...p,
                    imageFile: f || null,
                    imageUrl: "",
                  }));
                }}
              />
            </label>
            {newsForm.imageFile && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setNewsForm((p) => ({ ...p, imageFile: null }));
                    if (newsFileRef.current) newsFileRef.current.value = "";
                  }}
                  className="text-xs text-red-500 hover:text-red-700 transition"
                >
                  Kaldır
                </button>
                <img
                  src={URL.createObjectURL(newsForm.imageFile)}
                  alt="önizleme"
                  className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                />
              </>
            )}
          </div>
          {/* Ek Görseller (max 4) */}
          <AdminInput label="Ek Görseller (max 4)">
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => {
                const previewUrl = extraNewsImageFiles[i]
                  ? URL.createObjectURL(extraNewsImageFiles[i])
                  : newsForm.extraImages?.[i] || "";
                return (
                  <div key={i} className="flex items-center gap-2">
                    <AInput
                      value={
                        extraNewsImageFiles[i]
                          ? ""
                          : newsForm.extraImages?.[i] || ""
                      }
                      onChange={(e) => {
                        const updated = [...(newsForm.extraImages || [])];
                        updated[i] = e.target.value;
                        setNewsForm((p) => ({ ...p, extraImages: updated }));
                      }}
                      placeholder={`Görsel ${i + 1} URL… https://`}
                      disabled={!!extraNewsImageFiles[i]}
                    />
                    <label className="shrink-0 flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer text-xs text-slate-600 transition">
                      <ImageIcon className="w-3.5 h-3.5 text-slate-400" />
                      {extraNewsImageFiles[i]
                        ? extraNewsImageFiles[i].name.slice(0, 10) + "…"
                        : "Dosya"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={(el) => (extraNewsImageRefs.current[i] = el)}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const updated = [...extraNewsImageFiles];
                          updated[i] = f;
                          setExtraNewsImageFiles(updated);
                          const urls = [...(newsForm.extraImages || [])];
                          urls[i] = "";
                          setNewsForm((p) => ({ ...p, extraImages: urls }));
                        }}
                      />
                    </label>
                    {previewUrl && (
                      <div className="relative shrink-0">
                        <img
                          src={previewUrl}
                          alt={`görsel ${i + 1}`}
                          className="w-10 h-10 rounded-lg object-cover border border-slate-200"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updatedFiles = [...extraNewsImageFiles];
                            updatedFiles[i] = null;
                            setExtraNewsImageFiles(updatedFiles);
                            const urls = [...(newsForm.extraImages || [])];
                            urls[i] = "";
                            setNewsForm((p) => ({ ...p, extraImages: urls }));
                            if (extraNewsImageRefs.current[i])
                              extraNewsImageRefs.current[i].value = "";
                          }}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </AdminInput>
          <SubmitBtn loading={loading}>Haberi Kaydet</SubmitBtn>
        </form>
      );

    if (activeTab === "interviews")
      return (
        <form onSubmit={handleInterviewSubmit} className="space-y-4">
          <AdminInput label="Röportaj Başlığı">
            <AInput
              value={interviewForm.title}
              onChange={(e) =>
                setInterviewForm((p) => ({ ...p, title: e.target.value }))
              }
              placeholder="Başlık…"
            />
          </AdminInput>
          <div className="grid grid-cols-2 gap-3">
            <AdminInput label="Konuk">
              <AInput
                value={interviewForm.guest}
                onChange={(e) =>
                  setInterviewForm((p) => ({ ...p, guest: e.target.value }))
                }
              />
            </AdminInput>
            <AdminInput label="Röportajı Yapan">
              <AInput
                value={interviewForm.interviewer}
                onChange={(e) =>
                  setInterviewForm((p) => ({
                    ...p,
                    interviewer: e.target.value,
                  }))
                }
              />
            </AdminInput>
          </div>
          <AdminInput label="Kısa Özet">
            <ATextarea
              value={interviewForm.summary}
              onChange={(e) =>
                setInterviewForm((p) => ({ ...p, summary: e.target.value }))
              }
              rows={4}
            />
          </AdminInput>
          <AdminInput label="Video URL">
            <AInput
              value={interviewForm.videoUrl}
              onChange={(e) =>
                setInterviewForm((p) => ({ ...p, videoUrl: e.target.value }))
              }
              placeholder="https://youtube.com/…"
            />
          </AdminInput>
          <SubmitBtn loading={loading}>Röportajı Kaydet</SubmitBtn>
        </form>
      );

    if (activeTab === "celebrities")
      return (
        <form onSubmit={handleCelebritySubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <AdminInput label="İsim Soyisim">
              <AInput
                value={celebrityForm.name}
                onChange={(e) =>
                  setCelebrityForm((p) => ({ ...p, name: e.target.value }))
                }
              />
            </AdminInput>
            <AdminInput label="Kategori">
              <ASelect
                value={celebrityForm.kategori}
                onChange={(e) =>
                  setCelebrityForm((p) => ({
                    ...p,
                    kategori: e.target.value,
                    profession: "",
                  }))
                }
              >
                <option value="">Kategori seçin…</option>
                {ANA_KATEGORILER.filter((k) => k !== "siyasiler").map((k) => (
                  <option key={k} value={k}>
                    {toSentenceCase(k)}
                  </option>
                ))}
              </ASelect>
            </AdminInput>

            <AdminInput label="Meslek / Ünvan">
              {(UNLU_KATEGORI_DATA[celebrityForm.kategori] || []).length >
                0 && (
                <ASelect
                  value=""
                  onChange={(e) => {
                    if (e.target.value)
                      setCelebrityForm((p) => ({
                        ...p,
                        profession: e.target.value,
                      }));
                  }}
                >
                  <option value="">Alt kategori seçin…</option>
                  {(UNLU_KATEGORI_DATA[celebrityForm.kategori] || []).map(
                    (a) => (
                      <option key={a} value={a}>
                        {toSentenceCase(a)}
                      </option>
                    )
                  )}
                </ASelect>
              )}
              <div
                className={
                  (UNLU_KATEGORI_DATA[celebrityForm.kategori] || []).length > 0
                    ? "mt-2"
                    : ""
                }
              >
                <AInput
                  value={celebrityForm.profession}
                  onChange={(e) =>
                    setCelebrityForm((p) => ({
                      ...p,
                      profession: e.target.value,
                    }))
                  }
                  placeholder={
                    (UNLU_KATEGORI_DATA[celebrityForm.kategori] || []).length >
                    0
                      ? "Veya serbest yazın…"
                      : "Kategori seçin veya direkt yazın…"
                  }
                />
              </div>
            </AdminInput>
            <AdminInput label="Doğum Yılı">
              <AInput
                value={celebrityForm.birthYear}
                onChange={(e) =>
                  setCelebrityForm((p) => ({ ...p, birthYear: e.target.value }))
                }
                placeholder="1970"
              />
            </AdminInput>
            {/* Doğum Yeri */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <AdminInput label="İlçe">
                <ASelect
                  value={celebrityForm.sivasIlce}
                  onChange={(e) =>
                    setCelebrityForm((f) => ({
                      ...f,
                      sivasIlce: e.target.value,
                      sivasKoy: "",
                    }))
                  }
                >
                  <option value="">İlçe seçiniz…</option>
                  {ilceler.map((il) => (
                    <option key={il} value={il}>
                      {il}
                    </option>
                  ))}
                </ASelect>
              </AdminInput>

              <AdminInput label="Köy / Mahalle">
                <ASelect
                  value={celebrityForm.sivasKoy}
                  onChange={(e) =>
                    setCelebrityForm((f) => ({
                      ...f,
                      sivasKoy: e.target.value,
                    }))
                  }
                  disabled={!celebrityForm.sivasIlce}
                >
                  <option value="">
                    {celebrityForm.sivasIlce
                      ? "Köy seçiniz…"
                      : "Önce ilçe seçiniz"}
                  </option>
                  {(ilceKoyMap[celebrityForm.sivasIlce] || []).map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </ASelect>
              </AdminInput>
            </div>

            {/* Cinsiyet */}
            <AdminInput label="Cinsiyet">
              <div className="flex gap-3">
                {[
                  { value: "erkek", label: "Erkek" },
                  { value: "kadın", label: "Kadın" },
                ].map(({ value, label }) => (
                  <label
                    key={value}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border cursor-pointer text-sm font-medium transition ${
                      celebrityForm.gender === value
                        ? "bg-slate-900 text-white border-slate-900"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="radio"
                      className="hidden"
                      name="pol-gender"
                      value={value}
                      checked={celebrityForm.gender === value}
                      onChange={() =>
                        setCelebrityForm((f) => ({ ...f, gender: value }))
                      }
                    />
                    {label}
                  </label>
                ))}
                {celebrityForm.gender && (
                  <button
                    type="button"
                    onClick={() =>
                      setCelebrityForm((f) => ({ ...f, gender: "" }))
                    }
                    className="px-3 py-2 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50 text-xs transition"
                  >
                    Temizle
                  </button>
                )}
              </div>
            </AdminInput>
          </div>
          <AdminInput label="Kısa Biyografi">
            <ATextarea
              value={celebrityForm.bio}
              onChange={(e) =>
                setCelebrityForm((p) => ({ ...p, bio: e.target.value }))
              }
              rows={4}
            />
          </AdminInput>
          <AdminInput label="Fotoğraf URL">
            <AInput
              value={celebrityForm.imageUrl}
              onChange={(e) =>
                setCelebrityForm((p) => ({ ...p, imageUrl: e.target.value }))
              }
              placeholder="https://…"
            />
          </AdminInput>
          {/* Durum Seçimi */}
          <div className="flex items-center gap-2 pt-2 pb-1">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
              Durum
            </span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div className="flex flex-wrap gap-3">
            {[
              { key: "isActive", label: "Görevde", color: "bg-emerald-500" },
              {
                key: "isPassive",
                label: "Görevde Değil",
                color: "bg-slate-400",
              },
              { key: "isDeceased", label: "Vefat", color: "bg-red-400" },
            ].map(({ key, label, color }) => (
              <label
                key={key}
                className="flex items-center gap-3 cursor-pointer group w-fit"
              >
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={celebrityForm[key]}
                    onChange={(e) =>
                      setCelebrityForm((p) => ({
                        ...p,
                        [key]: e.target.checked,
                      }))
                    }
                    className="sr-only"
                  />
                  <div
                    className={cx(
                      "w-11 h-6 rounded-full transition-colors duration-200",
                      celebrityForm[key] ? color : "bg-slate-200"
                    )}
                  >
                    <div
                      className={cx(
                        "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200",
                        celebrityForm[key] ? "translate-x-5" : "translate-x-0"
                      )}
                    />
                  </div>
                </div>
                <span className="text-sm font-medium text-slate-700">
                  {label}
                </span>
              </label>
            ))}
          </div>
          <AdminInput label="Etiketler">
            <div className="flex gap-2">
              <AInput
                value={celebrityForm.tagInput}
                onChange={(e) =>
                  setCelebrityForm((p) => ({ ...p, tagInput: e.target.value }))
                }
                placeholder="Örn: türkü, bağlama, halk ozanı…"
              />
              <button
                type="button"
                onClick={() => {
                  const t = celebrityForm.tagInput.trim();
                  if (!t || celebrityForm.tags.includes(t))
                    return setCelebrityForm((p) => ({ ...p, tagInput: "" }));
                  setCelebrityForm((p) => ({
                    ...p,
                    tags: [...p.tags, t],
                    tagInput: "",
                  }));
                }}
                className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Ekle
              </button>
            </div>
            {celebrityForm.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {celebrityForm.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-medium"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() =>
                        setCelebrityForm((p) => ({
                          ...p,
                          tags: p.tags.filter((x) => x !== t),
                        }))
                      }
                      className="text-amber-600 hover:text-red-600 transition"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </AdminInput>
          <SubmitBtn loading={loading}>Ünlüyü Kaydet</SubmitBtn>
        </form>
      );

    if (activeTab === "politicians")
      return (
        <form onSubmit={handlePoliticianSubmit} className="space-y-4">
          {/* Temel Bilgiler */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <AdminInput label="İsim Soyisim">
              <AInput
                value={politicianForm.name}
                onChange={(e) =>
                  setPoliticianForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder="Ad Soyad"
              />
            </AdminInput>
            <AdminInput label="Parti">
              <AInput
                value={politicianForm.party}
                onChange={(e) =>
                  setPoliticianForm((p) => ({ ...p, party: e.target.value }))
                }
                placeholder="CHP, AKP, MHP…"
              />
            </AdminInput>
            <AdminInput label="Görev / Pozisyon">
              <ASelect
                value=""
                onChange={(e) => {
                  if (e.target.value)
                    setPoliticianForm((p) => ({
                      ...p,
                      position: e.target.value,
                    }));
                }}
              >
                <option value="">Görev seçin…</option>
                {(UNLU_KATEGORI_DATA["siyasiler"] || []).map((p) => (
                  <option key={p} value={p}>
                    {toSentenceCase(p)}
                  </option>
                ))}
              </ASelect>
              <div className="mt-2">
                <AInput
                  value={politicianForm.position}
                  onChange={(e) =>
                    setPoliticianForm((p) => ({
                      ...p,
                      position: e.target.value,
                    }))
                  }
                  placeholder="Veya serbest yazın…"
                />
              </div>
            </AdminInput>
            <AdminInput label="Doğum Yılı">
              <AInput
                value={politicianForm.birthYear}
                onChange={(e) =>
                  setPoliticianForm((p) => ({
                    ...p,
                    birthYear: e.target.value,
                  }))
                }
                placeholder="1965"
              />
            </AdminInput>
            {/* Doğum Yeri */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <AdminInput label="İlçe">
                <ASelect
                  value={politicianForm.sivasIlce}
                  onChange={(e) =>
                    setPoliticianForm((f) => ({
                      ...f,
                      sivasIlce: e.target.value,
                      sivasKoy: "",
                    }))
                  }
                >
                  <option value="">İlçe seçiniz…</option>
                  {ilceler.map((il) => (
                    <option key={il} value={il}>
                      {il}
                    </option>
                  ))}
                </ASelect>
              </AdminInput>

              <AdminInput label="Köy / Mahalle">
                <ASelect
                  value={politicianForm.sivasKoy}
                  onChange={(e) =>
                    setPoliticianForm((f) => ({
                      ...f,
                      sivasKoy: e.target.value,
                    }))
                  }
                  disabled={!politicianForm.sivasIlce}
                >
                  <option value="">
                    {politicianForm.sivasIlce
                      ? "Köy seçiniz…"
                      : "Önce ilçe seçiniz"}
                  </option>
                  {(ilceKoyMap[politicianForm.sivasIlce] || []).map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </ASelect>
              </AdminInput>
            </div>

            {/* Cinsiyet */}
            <AdminInput label="Cinsiyet">
              <div className="flex gap-3">
                {[
                  { value: "erkek", label: "Erkek" },
                  { value: "kadın", label: "Kadın" },
                ].map(({ value, label }) => (
                  <label
                    key={value}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border cursor-pointer text-sm font-medium transition ${
                      politicianForm.gender === value
                        ? "bg-slate-900 text-white border-slate-900"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="radio"
                      className="hidden"
                      name="pol-gender"
                      value={value}
                      checked={politicianForm.gender === value}
                      onChange={() =>
                        setPoliticianForm((f) => ({ ...f, gender: value }))
                      }
                    />
                    {label}
                  </label>
                ))}
                {politicianForm.gender && (
                  <button
                    type="button"
                    onClick={() =>
                      setPoliticianForm((f) => ({ ...f, gender: "" }))
                    }
                    className="px-3 py-2 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50 text-xs transition"
                  >
                    Temizle
                  </button>
                )}
              </div>
            </AdminInput>
          </div>

          <AdminInput label="Biyografi">
            <ATextarea
              value={politicianForm.bio}
              onChange={(e) =>
                setPoliticianForm((p) => ({ ...p, bio: e.target.value }))
              }
              rows={4}
              placeholder="Kısa biyografi…"
            />
          </AdminInput>

          <AdminInput label="Fotoğraf URL">
            <AInput
              value={politicianForm.imageUrl}
              onChange={(e) =>
                setPoliticianForm((p) => ({ ...p, imageUrl: e.target.value }))
              }
              placeholder="https://…"
            />
          </AdminInput>

          {/* Durum Seçimi */}
          <div className="flex items-center gap-2 pt-2 pb-1">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
              Durum
            </span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div className="flex flex-wrap gap-3">
            {[
              { key: "isActive", label: "Görevde", color: "bg-emerald-500" },
              {
                key: "isPassive",
                label: "Görevde Değil",
                color: "bg-slate-400",
              },
              { key: "isDeceased", label: "Vefat", color: "bg-red-400" },
            ].map(({ key, label, color }) => (
              <label
                key={key}
                className="flex items-center gap-3 cursor-pointer group w-fit"
              >
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={politicianForm[key]}
                    onChange={(e) =>
                      setPoliticianForm((p) => ({
                        ...p,
                        [key]: e.target.checked,
                      }))
                    }
                    className="sr-only"
                  />
                  <div
                    className={cx(
                      "w-11 h-6 rounded-full transition-colors duration-200",
                      politicianForm[key] ? color : "bg-slate-200"
                    )}
                  >
                    <div
                      className={cx(
                        "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200",
                        politicianForm[key] ? "translate-x-5" : "translate-x-0"
                      )}
                    />
                  </div>
                </div>
                <span className="text-sm font-medium text-slate-700">
                  {label}
                </span>
              </label>
            ))}
          </div>

          {/* Etiketler */}
          <div className="flex items-center gap-2 pt-2 pb-1">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
              Etiketler
            </span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div>
            <div className="flex gap-2">
              <AInput
                value={politicianForm.tagInput}
                onChange={(e) =>
                  setPoliticianForm((p) => ({ ...p, tagInput: e.target.value }))
                }
                placeholder="Örn: milletvekili, yerel yönetim…"
              />
              <button
                type="button"
                onClick={() => {
                  const t = politicianForm.tagInput.trim();
                  if (!t || politicianForm.tags.includes(t))
                    return setPoliticianForm((p) => ({ ...p, tagInput: "" }));
                  setPoliticianForm((p) => ({
                    ...p,
                    tags: [...p.tags, t],
                    tagInput: "",
                  }));
                }}
                className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Ekle
              </button>
            </div>
            {politicianForm.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {politicianForm.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-200 rounded-full text-xs"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() =>
                        setPoliticianForm((p) => ({
                          ...p,
                          tags: p.tags.filter((x) => x !== t),
                        }))
                      }
                      className="text-slate-400 hover:text-red-500 transition"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <SubmitBtn loading={loading}>Siyasetçiyi Kaydet</SubmitBtn>
        </form>
      );
    if (activeTab === "photos")
      return (
        <form onSubmit={handlePhotoSubmit} className="space-y-4">
          <AdminInput label="Başlık">
            <AInput
              value={photoForm.title}
              onChange={(e) =>
                setPhotoForm((p) => ({ ...p, title: e.target.value }))
              }
            />
          </AdminInput>
          <AdminInput label="Açıklama">
            <ATextarea
              value={photoForm.description}
              onChange={(e) =>
                setPhotoForm((p) => ({ ...p, description: e.target.value }))
              }
              rows={3}
            />
          </AdminInput>
          <AdminInput label="Konum / Köy / İlçe">
            <AInput
              value={photoForm.location}
              onChange={(e) =>
                setPhotoForm((p) => ({ ...p, location: e.target.value }))
              }
              placeholder="Zara / Ekinli Köyü"
            />
          </AdminInput>

          {/* Görsel: URL veya dosya — ikisi birbirini dışlar */}
          <AdminInput label="Görsel URL">
            <AInput
              value={photoForm.imageUrl}
              onChange={(e) =>
                setPhotoForm((p) => ({
                  ...p,
                  imageUrl: e.target.value,
                  imageFile: null,
                }))
              }
              placeholder="https://…"
              disabled={!!photoForm.imageFile}
            />
          </AdminInput>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-xs text-slate-400">veya galeriden seç</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer text-sm text-slate-700 transition">
              <ImageIcon className="w-4 h-4 text-slate-400" />
              {photoForm.imageFile ? photoForm.imageFile.name : "Dosya Seç"}
              <input
                ref={photoFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setPhotoForm((p) => ({
                    ...p,
                    imageFile: f || null,
                    imageUrl: "",
                  }));
                }}
              />
            </label>
            {photoForm.imageFile && (
              <button
                type="button"
                onClick={() => {
                  setPhotoForm((p) => ({ ...p, imageFile: null }));
                  if (photoFileRef.current) photoFileRef.current.value = "";
                }}
                className="text-xs text-red-500 hover:text-red-700 transition"
              >
                Kaldır
              </button>
            )}
            {/* Önizleme */}
            {photoForm.imageFile && (
              <img
                src={URL.createObjectURL(photoForm.imageFile)}
                alt="önizleme"
                className="w-12 h-12 rounded-lg object-cover border border-slate-200"
              />
            )}
          </div>

          <SubmitBtn loading={loading}>Resmi Kaydet</SubmitBtn>
        </form>
      );

    if (activeTab === "videos")
      return (
        <form onSubmit={handleVideoSubmit} className="space-y-4">
          <AdminInput label="Başlık">
            <AInput
              value={videoForm.title}
              onChange={(e) =>
                setVideoForm((p) => ({ ...p, title: e.target.value }))
              }
            />
          </AdminInput>
          <AdminInput label="Açıklama">
            <ATextarea
              value={videoForm.description}
              onChange={(e) =>
                setVideoForm((p) => ({ ...p, description: e.target.value }))
              }
              rows={3}
            />
          </AdminInput>

          <AdminInput label="Video URL (YouTube / Vimeo)">
            <AInput
              value={videoForm.videoUrl}
              onChange={(e) =>
                setVideoForm((p) => ({
                  ...p,
                  videoUrl: e.target.value,
                  videoFile: null,
                }))
              }
              placeholder="https://youtube.com/…"
              disabled={!!videoForm.videoFile}
            />
          </AdminInput>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-xs text-slate-400">veya galeriden yükle</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer text-sm text-slate-700 transition">
              <Video className="w-4 h-4 text-slate-400" />
              {videoForm.videoFile
                ? videoForm.videoFile.name
                : "Video Dosyası Seç"}
              <input
                ref={videoFileRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setVideoForm((p) => ({
                    ...p,
                    videoFile: f || null,
                    videoUrl: "",
                  }));
                }}
              />
            </label>
            {videoForm.videoFile && (
              <button
                type="button"
                onClick={() => {
                  setVideoForm((p) => ({ ...p, videoFile: null }));
                  if (videoFileRef.current) videoFileRef.current.value = "";
                }}
                className="text-xs text-red-500 hover:text-red-700 transition"
              >
                Kaldır
              </button>
            )}
          </div>

          <AdminInput label="Thumbnail URL (opsiyonel)">
            <AInput
              value={videoForm.thumbnailUrl}
              onChange={(e) =>
                setVideoForm((p) => ({ ...p, thumbnailUrl: e.target.value }))
              }
              placeholder="https://…"
            />
          </AdminInput>

          <SubmitBtn loading={loading}>Videoyu Kaydet</SubmitBtn>
        </form>
      );
  };

  const activeTabMeta = TABS.find((t) => t.id === activeTab);
  const ActiveIcon = activeTabMeta?.icon || LayoutDashboard;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Üst bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5 text-amber-500" />
            <span className="font-semibold text-slate-900">
              İsteSivas Admin Panel
            </span>
          </div>
          <span className="text-xs text-slate-400">
            Sadece yetkili kullanıcılar içindir
          </span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-5">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 bg-white rounded-2xl border border-slate-200 shadow-sm p-3 h-max sticky top-20">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest px-2 mb-2">
            İçerik Tipleri
          </p>
          <nav className="space-y-0.5">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cx(
                    "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition",
                    active
                      ? "bg-slate-900 text-white shadow-sm"
                      : "text-slate-700 hover:bg-slate-50"
                  )}
                >
                  <Icon
                    className={cx(
                      "w-4 h-4",
                      active ? "text-amber-400" : "text-slate-400"
                    )}
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Ana içerik */}
        <main className="flex-1 min-w-0">
          {status && (
            <div
              className={cx(
                "mb-4 px-4 py-3 rounded-xl text-sm border",
                status.type === "success"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-red-50 border-red-200 text-red-700"
              )}
            >
              {status.msg}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-5">
              <ActiveIcon className="w-5 h-5 text-amber-500" />
              <h2 className="text-base font-semibold text-slate-900">
                {activeTabMeta?.label}
              </h2>
            </div>
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

function SubmitBtn({ loading, children }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50"
    >
      <Save className="w-4 h-4" />
      {loading ? "Kaydediliyor…" : children}
    </button>
  );
}

export default AdminPanel;
